import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Alert, Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup,
  IconButton, Link, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  PersonAddAlt1 as AddIcon, DeleteOutline as RemoveIcon,
} from '@mui/icons-material';
import * as hierarchyApi from '../api/hierarchy';

/**
 * Who administers this level. An enterprise, organisation or region can have several,
 * and they are peers: each can appoint another and each can be removed by another.
 *
 * Two refusals come back from the server rather than being pre-empted here, because
 * they are facts about the account rather than the form: you cannot remove the last
 * administrator, and you cannot remove your own access. Letting the API say so keeps
 * one rule in one place.
 */
export default function NodeAdmins() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);        // { email, full_name }
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // Region only: choosing where an admin also practises. { admin, picked: Set<clinicId> }
  const [practise, setPractise] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await hierarchyApi.listAdmins());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load administrators');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const email = (invite?.email || '').trim();
    if (!email) { toast.error('An email address is required'); return; }
    setSaving(true);
    try {
      const res = await hierarchyApi.addAdmin({ email, full_name: invite.full_name || null });
      toast.success(res.message || 'Invitation sent');
      setInvite(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add that administrator');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    setBusyId(row.user_id);
    try {
      await hierarchyApi.removeAdmin(row.user_id);
      toast.success(`${row.full_name || row.email} no longer administers this account`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove them');
    } finally {
      setBusyId(null);
    }
  };

  const saveOffices = async (row, ids) => {
    setBusyId(row.user_id);
    try {
      const res = await hierarchyApi.setAdminProviderOffices(row.user_id, ids);
      toast.success(res.message || 'Updated');
      setPractise(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update where they practise');
    } finally {
      setBusyId(null);
    }
  };

  const toggleProvider = (row, on) => {
    if (!on) { saveOffices(row, []); return; }
    // Turning it on asks where: every office in the region, unless they narrow it.
    setPractise({ admin: row, picked: new Set((data?.offices || []).map((o) => o.id)) });
  };

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!data) return null;

  const { node, admins = [], offices = [] } = data;
  const isRegion = node?.scope === 'region';
  const officeName = Object.fromEntries(offices.map((o) => [o.id, o.name]));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start"
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Administrators</Typography>
          <Typography variant="body2" color="text.secondary">
            Who runs {node?.name}. They manage its structure and its AI sessions.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setInvite({ email: '', full_name: '' })}>
          Add administrator
        </Button>
      </Stack>

      {node?.scope === 'enterprise' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Enterprise administrators manage organisations, regions and AI sessions, and
          can open any organisation, region or office of the enterprise to work inside it
          as its administrator — patients included, and every opening recorded. From
          the enterprise itself they see only the patients an office has chosen to share
          upward, read-only.
        </Alert>
      )}

      <Card>
        <Divider />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="center">Status</TableCell>
                {isRegion && <TableCell>Provider</TableCell>}
                <TableCell align="center">Remove</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!admins.length ? (
                <TableRow><TableCell colSpan={isRegion ? 5 : 4}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No administrators yet.
                  </Typography>
                </TableCell></TableRow>
              ) : admins.map((a) => (
                <TableRow key={a.user_id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        {a.full_name || '—'}
                      </Typography>
                      {a.is_you && <Chip size="small" label="You" sx={{ height: 20 }} />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{a.email}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" sx={{ height: 22 }}
                      label={a.pending_setup ? 'Invited' : a.is_active ? 'Active' : 'Inactive'}
                      color={a.pending_setup ? 'warning' : a.is_active ? 'success' : 'default'} />
                  </TableCell>
                  {isRegion && (
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title={a.provider_at?.length
                          ? 'Stop practising — they stay a region administrator'
                          : 'Let them see patients and run sessions as a provider at offices in this region'}>
                          <span>
                            <Switch size="small" color="secondary"
                              checked={Boolean(a.provider_at?.length)}
                              disabled={busyId === a.user_id || !offices.length}
                              onChange={(e) => toggleProvider(a, e.target.checked)} />
                          </span>
                        </Tooltip>
                        {a.provider_at?.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                              {a.provider_at.length === offices.length
                                ? 'All offices'
                                : a.provider_at.map((id) => officeName[id]).filter(Boolean).join(', ')}
                            </Typography>
                            <Link component="button" variant="caption" underline="hover"
                              onClick={() => setPractise({ admin: a, picked: new Set(a.provider_at) })}>
                              Change offices
                            </Link>
                          </Box>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Tooltip title={a.is_you
                      ? 'You cannot remove your own access'
                      : `Remove ${a.full_name || a.email}`}>
                      <span>
                        <IconButton size="small" color="error"
                          disabled={a.is_you || busyId === a.user_id}
                          onClick={() => remove(a)}>
                          {busyId === a.user_id
                            ? <CircularProgress size={16} />
                            : <RemoveIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={Boolean(practise)} onClose={() => busyId == null && setPractise(null)}
        maxWidth="xs" fullWidth>
        <DialogTitle>Where does {practise?.admin.full_name || practise?.admin.email} practise?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            At the offices you tick they can see patients, run scribe sessions and write
            notes as a provider, and patients can book them. They stay a region
            administrator either way.
          </Typography>
          <FormGroup>
            {offices.map((o) => (
              <FormControlLabel key={o.id} label={o.name}
                control={(
                  <Checkbox checked={practise?.picked.has(o.id) || false}
                    onChange={(e) => {
                      const next = new Set(practise.picked);
                      if (e.target.checked) next.add(o.id); else next.delete(o.id);
                      setPractise({ ...practise, picked: next });
                    }} />
                )} />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPractise(null)} disabled={busyId != null}>Cancel</Button>
          <Button variant="contained" disabled={busyId != null}
            onClick={() => saveOffices(practise.admin, [...practise.picked])}>
            {practise?.picked.size ? 'Save' : 'Remove provider role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(invite)} onClose={() => !saving && setInvite(null)}
        maxWidth="xs" fullWidth>
        <DialogTitle>Add an administrator</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If they already have a ScribeLive account, this adds the role to it — one
            person, one password. Otherwise they are sent an invitation.
          </Typography>
          <TextField autoFocus fullWidth label="Email" margin="dense"
            value={invite?.email || ''}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
          <TextField fullWidth label="Full name" margin="dense"
            value={invite?.full_name || ''}
            onChange={(e) => setInvite({ ...invite, full_name: e.target.value })}
            helperText="They can change it when they set their password." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInvite(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
